import Vue from 'vue'
import Router from 'vue-router'
import { Message } from 'element-ui'
import { getToken, getUserInfo } from '@/utils/storage'
import { canAccessRoute } from '@/utils/permission'
import { getEmployeeMenusCached } from '@/api/system'
import adminRoutes from './admin'
import employeeRoutes from './employee'
import store from '@/store'
import { APP_TITLE } from '@/utils/appConfig'

Vue.use(Router)

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login/index.vue'),
    meta: { title: '登录' }
  },
  {
    path: '/',
    redirect: '/login'
  },
  ...adminRoutes,
  ...employeeRoutes,
  {
    path: '*',
    redirect: (to) => {
      // 避免把 /uploads/* 资源请求重定向到 login（双保险：配合 beforeEach）
      if (to && to.path && to.path.startsWith('/uploads/')) {
        return to.fullPath || to.path
      }
      return '/login'
    }
  }
]

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

/**
 * vue-router 3 在登录/守卫重定向场景常见两个 Promise 未 catch 的 Uncaught 错误：
 *  1) NavigationDuplicated（重复 push 同一路径）
 *  2) NavigationRedirected（"Redirected when going from '/login' to '/admin/dashboard' via a navigation guard"）
 * 下面统一吞掉上述"非业务错误型"的 reject，避免开发环境 red overlay 和控制台噪声。
 */
const originalPush = Router.prototype.push
const originalReplace = Router.prototype.replace
function isSilentRouterError(err) {
  if (!err) return true
  const name = (err && err.name) || ''
  const msg = (err && err.message) || ''
  return (
    name === 'NavigationDuplicated' ||
    name === 'NavigationRedirected' ||
    msg.indexOf('Redirected when going') !== -1 ||
    msg.indexOf('NavigationDuplicated') !== -1 ||
    msg.indexOf('Avoided redundant navigation') !== -1
  )
}
Router.prototype.push = function (location, onComplete, onAbort) {
  if (typeof onComplete === 'function' || typeof onAbort === 'function') {
    return originalPush.call(this, location, onComplete, onAbort)
  }
  try {
    const p = originalPush.call(this, location)
    if (p && typeof p.catch === 'function') {
      return p.catch((err) => {
        if (isSilentRouterError(err)) return undefined
        throw err
      })
    }
    return p
  } catch (err) {
    if (isSilentRouterError(err)) return undefined
    throw err
  }
}
Router.prototype.replace = function (location, onComplete, onAbort) {
  if (typeof onComplete === 'function' || typeof onAbort === 'function') {
    return originalReplace.call(this, location, onComplete, onAbort)
  }
  try {
    const p = originalReplace.call(this, location)
    if (p && typeof p.catch === 'function') {
      return p.catch((err) => {
        if (isSilentRouterError(err)) return undefined
        throw err
      })
    }
    return p
  } catch (err) {
    if (isSilentRouterError(err)) return undefined
    throw err
  }
}

// 员工端菜单路径 → 菜单键映射（前缀匹配，覆盖列表下的子页面）
const EMPLOYEE_MENU_PATHS = [
  ['/employee/my-salary', 'my-salary'],
  ['/employee/my-requests', 'my-requests'],
  ['/employee/my-approvals', 'my-approvals'],
  ['/employee/team-appraisal', 'team-appraisal'],
  ['/employee/my-appraisal', 'my-appraisal'],
  ['/employee/feedback', 'feedback'],
  ['/employee/profile', 'profile']
]

router.beforeEach(async (to, from, next) => {
  document.title = to.meta.title ? to.meta.title + ' - ' + APP_TITLE : APP_TITLE
  // 静态资源路径：中止 SPA 路由，让浏览器发起真实 HTTP 请求。
  if (to.path && to.path.startsWith('/uploads/')) {
    next(false)
    try {
      if (typeof window !== 'undefined') {
        window.location.replace((to.fullPath || to.path))
      }
    } catch (e) {}
    return
  }

  // 登录页不需要鉴权
  if (to.path === '/login') {
    next()
    return
  }

  // 没有 token → 跳登录，并保存原访问路径用于登录后还原
  // 注意：不要手动 encodeURIComponent，Vue Router 在拼 query 时会自动编码一次
  // 手动再 encode 会导致双重编码（%2F → %252F），Login.vue 匹配时会失败
  const token = getToken()
  if (!token) {
    const fullPath = to.fullPath || to.path
    next({
      path: '/login',
      query: { redirect: fullPath }
    })
    return
  }

  // 有 token 但没有 userInfo → 立即从后端拉一次，同步强制改密码标记
  const cachedInfo = getUserInfo()
  if (!cachedInfo || !cachedInfo.role) {
    try {
      await store.dispatch('user/getInfo')
    } catch (e) {
      // 拉取失败（如 token 过期）→ 回登录，保留原访问路径便于登录后跳回（如邮件里的工资条链接）
      next({ path: '/login', query: { redirect: to.fullPath || to.path } })
      return
    }
  } else if (!cachedInfo.mustChangePassword) {
    // 若缓存里没有"强制改密码"标记，则后台刷新一次（避免管理员刚设置强制改密时，用户仍停留在旧缓存）
    // 不阻塞页面跳转，App.vue 的 Dialog 会在 store 更新后自动开
    store.dispatch('user/getInfo').catch(() => {})
  }

  // 从 store 中拿最新 userInfo（已通过 getInfo 或 缓存 写入）
  const userInfo = store.state.user.userInfo || getUserInfo()
  if (!userInfo || !userInfo.role) {
    next()
    return
  }

  // 角色路由保护（如 meta.role=admin 的角色管理页，meta.role=employee 的员工端）
  const routeRole = to.meta && to.meta.role
  if (routeRole && routeRole !== userInfo.role) {
    const target = userInfo.role === 'employee' ? '/employee/dashboard' : '/admin/dashboard'
    if (to.path !== target) {
      next(target)
      return
    }
  }

  // 员工端菜单显隐：被隐藏的菜单即使直接输入地址也不可访问（工作台固定可用）
  if (to.path && to.path.startsWith('/employee/')) {
    const hit = EMPLOYEE_MENU_PATHS.find(([p]) => to.path === p || to.path.startsWith(p + '/'))
    if (hit) {
      try {
        const menus = await getEmployeeMenusCached()
        const target = menus.find(m => m.menuKey === hit[1])
        // 默认隐藏，仅显式开启的菜单可访问
        if (!target || target.visible !== true) {
          Message.warning('该功能未开放')
          next('/employee/dashboard')
          return
        }
      } catch (e) {
        // 配置拉取失败时放行，避免网络抖动把用户锁死在工作台
      }
    }
  }

  // 管理端路由（/admin/**）按菜单级权限校验：sub_admin 必须对应 read=true 才能访问
  if (to.path && to.path.startsWith('/admin/') && userInfo.role !== 'admin') {
    if (userInfo.role === 'employee') {
      next('/employee/dashboard')
      return
    }
    // sub_admin：首次进入且权限字典为空（AdminLayout.created 还没跑）→ 在此同步阻塞加载，避免判定不准导致死循环
    if (userInfo.role === 'sub_admin') {
      const perms = (store.state.user && store.state.user.menuPermissions) || {}
      const keys = Object.keys(perms)
      if (keys.length === 0) {
        try { await store.dispatch('user/loadPermissions') } catch (e) {}
      }
    }
    // sub_admin：按 canAccessRoute 判定，无权限跳 dashboard（但已在 dashboard 上时直接放行，避免 next 自跳死循环栈溢出）
    if (!canAccessRoute(to)) {
      if (to.path === '/admin/dashboard') {
        next()
        return
      }
      next('/admin/dashboard')
      return
    }
  }

  next()
})

export default router
